import React, { useState, useEffect, useRef } from "react";

// Animated Counter Component
const AnimatedCounter = ({ value, duration = 2000, suffix = "" }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  // Extract numeric value and suffix from the original value
  const numericValue = parseFloat(value.replace(/[^\d.]/g, ''));
  const originalSuffix = value.replace(/[\d.]/g, '') || suffix;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) return;

    const startTime = Date.now();
    const startValue = 0;
    
    const animate = () => {
      const currentTime = Date.now();
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Easing function for smoother animation
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentValue = startValue + (numericValue - startValue) * easeOutQuart;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplayValue(numericValue);
      }
    };
    
    animate();
  }, [isVisible, numericValue, duration]);

  const formatValue = (val) => {
    if (originalSuffix === '%') {
      return Math.round(val);
    } else if (originalSuffix === 'x') {
      return val.toFixed(1);
    }
    return Math.round(val);
  };

  return (
    <span ref={elementRef} className="text-4xl font-bold text-gray-900 mb-1 inline-block">
      {formatValue(displayValue)}{originalSuffix}
    </span>
  );
};

// Animated Dots Component
const AnimatedDots = ({ dots, cardIndex }) => {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isVisible) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => observer.disconnect();
  }, [isVisible]);

  return (
    <div ref={elementRef} className="grid grid-cols-6 gap-1.5 max-w-28">
      {dots.map((dot, dotIdx) => (
        <div
          key={dotIdx}
          className={`w-2.5 h-2.5 rounded-full transition-all duration-500 transform ${
            isVisible ? 'scale-100 opacity-100' : 'scale-75 opacity-50'
          }`}
          style={{ 
            backgroundColor: isVisible ? dot.color : '#E5E7EB',
            transitionDelay: isVisible ? `${cardIndex * 100 + dotIdx * 80}ms` : '0ms',
            animation: isVisible && dot.color === '#3B82F6' ? `pulse-dot 2s ease-in-out ${cardIndex * 100 + dotIdx * 80}ms` : 'none'
          }}
        />
      ))}
    </div>
  );
};

const metrics = [
  {
    title: "CAREER ADVANCEMENT SPEED",
    value: "10x",
    subtitle: "FASTER GROWTH",
    description: "Our graduates achieve in 6 months what takes others 5 years. Break through career plateaus with 1to10x.",
    visualDots: Array.from({ length: 10 }, (_, i) => ({
      color: i < 8 ? "#3B82F6" : "#D1D5DB",
      position: i
    }))
  },
  {
    title: "SKILL GROWTH",
    value: "3.5x",
    subtitle: "MORE PRACTICE",
    description: "Build confidence through repeated practice, mentor feedback, and applied project work.",
    visualDots: Array.from({ length: 8 }, (_, i) => ({
      color: i < 7 ? "#3B82F6" : "#D1D5DB",
      position: i
    }))
  },
  {
    title: "LEARNING EFFICIENCY",
    value: "8x",
    subtitle: "MORE EFFECTIVE",
    description: "Master complex skills faster with our proven methodology. Why struggle for years when you can excel in months?",
    visualDots: Array.from({ length: 10 }, (_, i) => ({
      color: i < 8 ? "#3B82F6" : "#D1D5DB",
      position: i
    }))
  },
  {
    title: "PRACTICAL READINESS",
    value: "100%",
    subtitle: "PROJECT READY",
    description: "Build real-world skills through guided practice, applied projects, and practical feedback.",
    visualDots: Array.from({ length: 8 }, (_, i) => ({
      color: i < 8 ? "#3B82F6" : "#D1D5DB",
      position: i
    }))
  },
  {
    title: "CAREER CONFIDENCE",
    value: "10x",
    subtitle: "MORE CONFIDENT",
    description: "Transform from uncertain to unstoppable. Join the elite who shape the future of technology.",
    visualDots: Array.from({ length: 12 }, (_, i) => ({
      color: i < 10 ? "#3B82F6" : "#D1D5DB",
      position: i
    }))
  },
];

const WhyChooseUs = () => (
  <section className="py-20 px-4" style={{ backgroundColor: '#F1EFED' }}>
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="text-center mb-16">
        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
          <span className="text-blue-600">Transform</span>, <span className="text-blue-600">excel</span>, and <span className="text-blue-600">dominate</span>.
        </h2>
        <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
          The 1to10x advantage.
        </p>
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {metrics.map((metric, idx) => (
          <div 
            key={idx} 
            className="bg-white rounded-2xl p-4 sm:p-6 border border-gray-200 hover:shadow-lg transition-all duration-200 hover:-translate-y-1"
          >
            {/* Header */}
            <div className="mb-3 sm:mb-4">
              <h3 className="text-xs font-bold text-gray-500 mb-2 sm:mb-3 tracking-wide uppercase">
                {metric.title}
              </h3>
              
              {/* Animated Visual Dots */}
              <div className="mb-3 sm:mb-4">
                <AnimatedDots dots={metric.visualDots} cardIndex={idx} />
              </div>
            </div>

            {/* Metric Value */}
            <div className="mb-3">
              <div>
                <AnimatedCounter value={metric.value} duration={2500} />
              </div>
              <div className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide">
                {metric.subtitle}
              </div>
            </div>

            {/* Description */}
            <div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                {metric.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* CSS for dot animations */}
    <style>{`
      @keyframes pulse-dot {
        0% {
          transform: scale(0.75);
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.7);
        }
        50% {
          transform: scale(1.1);
          box-shadow: 0 0 0 6px rgba(59, 130, 246, 0);
        }
        100% {
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(59, 130, 246, 0);
        }
      }
    `}</style>
  </section>
);

export default WhyChooseUs; 
